import classes from './MarketCollectionCard.module.scss'
import classNames from 'classnames'
import ImageReveal from '@/components/ImageReveal'
import { dataLayerSelectProduct, dataLayerQuickAddClick } from '@/utils/dataLayer'
import { useRouter } from 'next/router'
import { useState, useMemo, useCallback } from 'react'
import ReviewStarsWithCounter from '@/components/Review/ReviewStarsWithCounter/ReviewStarsWithCounter'
import useProductReviewsDetails from '@/hooks/useProductReviewsDetails'
import { usePDPDrawerContext } from '@/context/PDPDrawerContext'
import ProductHeroPricing from '@/components/Sections/ProductHero/ProductHeroPricing'
import { InfoOutlined } from '@mui/icons-material'
import { useHeadlessCheckoutContext } from '@/context/HeadlessCheckoutContext'
import imageUrlBuilder from '@sanity/image-url'
import sanityClient from '@/services/sanityClient'

function MarketCollectionCard({
  title,
  description,
  images,
  as = 'list-item',
  cardType = 'default',
  location,
  hoverFeature,
  product,
  useFlyout,
  usePricing,
  useATC,
  sanityImages,
  position,
}) {
  const isGiftCard = product?.handle?.includes('digital-gift-card')
  const isGiftSubscription = product?.tags?.find((tag) => tag.toLowerCase()?.includes('gift'))
  const isGift = isGiftCard || isGiftSubscription
  const reviewsInfo = useProductReviewsDetails(product?.handle)
  const { openProductDetailDrawer } = usePDPDrawerContext()
  const { addItemToOrder } = useHeadlessCheckoutContext()
  const isBundleProduct = product?.tags?.some((tag) => tag.toLowerCase()?.includes('bundle'))
  const mapImage = useCallback(
    (productImage) => {
      const builder = imageUrlBuilder(sanityClient)
      return builder
        .image(productImage.asset.url)
        .width(1000)
        .height(1000)
        .focalPoint(
          productImage?.hotspot?.x || 0.5,
          productImage?.hotspot?.y || 0.5,
        )
        .crop('focalpoint')
        .fit('crop')
        .url()
    },
    [images?.edges?.length, sanityImages?.length],
  )

  const [fistImage, secondImage] = useMemo(() => {
    if (!sanityImages?.length) {
      return [
        images?.edges?.[0]?.node.originalSrc || images?.[0]?.src,
        images?.edges?.[1]?.node.originalSrc || images?.[1]?.src,
      ]
    }

    return sanityImages
      ?.slice(0, 2)
      .map(({ productImage }) => productImage)
      .map(mapImage)
  }, [images?.edges?.length, sanityImages?.length])

  const [onMoveEvent, setOnMoveEvent] = useState(null)
  const router = useRouter()
  const Element = as === 'list-item' ? 'li' : 'div'

  const flagTag = product?.tags?.find((tag) =>
    tag.toLowerCase()?.includes('flag:')
  )
  const splitFlagTag = flagTag?.split(':')[1]

  const onClickMainButton = async () => {
    // If "useATC" adds the product to cart
    if (useATC && !isGift) {
      dataLayerQuickAddClick({
        product,
        variant: product?.variants?.[0],
        url: router.asPath,
        context: 'marketplace_quick_add',
        position,
      })
      await addItemToOrder({
        product,
        variant: product.variants?.[0],
        openFlyout: true,
      })
      return
    } else if (isGift) {
      onClickCardContent()
      return
    }

    if (useFlyout) {
      openProductDetailDrawer({ productData: product })
    } else {
      dataLayerSelectProduct({ product, url: router.pathname })
      router.push(`/products/${encodeURIComponent(product.content?.handle)}`)
    }
  }
  const onClickCardContent = async () => {
      dataLayerSelectProduct({ product, url: router.pathname })
      router.push(`/products/${encodeURIComponent(product.content?.handle)}`)

  }

  const onLearnMoreClick = () => {
    openProductDetailDrawer({ productData: product })
  }

  return (
    <div className={classes.wrapper}>
      {splitFlagTag && (
        <div className={classes['best-seller']}>
          {splitFlagTag}
        </div>
      )}
      <Element className={classes.card}>
        <div className={classes.imageContainer}
        onClick={() => {
          onClickCardContent()
        }}
        >
          <ImageReveal
            parentOnMove={onMoveEvent}
            firstImage={fistImage}
            secondImage={secondImage}
            hoverFeature={hoverFeature}
          />
        </div>

        <div className={classes.contentWrapper}>
          <div className={classes.card__content}
          onClick={() => {
              onClickCardContent()
          }}
          >
            <div className={classes.card__content__header}>
              <h3 className={classes.card__content__title}>
                {title}
                {location && (
                  <span className={classes.card__content__location}>
                    {location}
                  </span>
                )}
              </h3>
              {usePricing && (
                <ProductHeroPricing
                  product={product}
                  selectedVariant={product.variants?.[0]}
                  pricingDisplay={product.sanityData?.pricingDisplay}
                  variant="market"
                  isBundleProduct={isBundleProduct}
                />
              )}
            </div>

            {reviewsInfo && (
              <ReviewStarsWithCounter
                rating={reviewsInfo.average}
                numberOfReviews={reviewsInfo.totalReviews}
              />
            )}

            <span className={classes.card__content__description}>
              {description}
            </span>
          </div>

          <button
            onClick={onClickMainButton}
            className={classes.card__button}
          >
            {useATC && !isGift ? 'Add to Basket' : 'View Details'}
          </button>
        </div>
      </Element>
    </div>
  )
}

export default MarketCollectionCard
